import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { allDocuments } from "@/lib/tree";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Zap, Coffee, FlaskConical, Play, Pause, Square, Calculator, Loader2, Clock, AlertTriangle, Coins } from "lucide-react";

const MODES = [
  { id: "espresso", name: "Espresso", icon: Zap, tag: "Fast & economical", desc: "Drafts, blogs, simple educational content." },
  { id: "double_espresso", name: "Double Espresso", icon: Coffee, tag: "Balanced default", desc: "Thorough, well-structured, readable." },
  { id: "ristretto", name: "Ristretto", icon: FlaskConical, tag: "Max reasoning", desc: "Technical, research, complex chapters." },
];

export default function GeneratePanel({ projectId, project, tree, status, reloadStatus, reloadTree }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [mode, setMode] = useState(project.default_mode || "double_espresso");
  const [scope, setScope] = useState("full");
  const [moduleId, setModuleId] = useState("");
  const [estimate, setEstimate] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [preflightOpen, setPreflightOpen] = useState(false);
  const [preflight, setPreflight] = useState(null);
  const [balance, setBalance] = useState(null);
  const [unlimited, setUnlimited] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [dailyBatches, setDailyBatches] = useState(false);
  const [meBalance, setMeBalance] = useState(null);
  const [meUnlimited, setMeUnlimited] = useState(false);
  const free = isAdmin || unlimited;

  useEffect(() => {
    api.get("/billing/me").then((r) => {
      setMeBalance(r.data.balance);
      setMeUnlimited(!!r.data.unlimited);
    }).catch(() => {});
  }, []);

  const activeJob = status?.active_job;
  const counts = status?.counts || {};
  const modules = tree.filter((n) => n.type === "module");
  // Authoritative doc count comes from /status (fast count_documents). The full node tree can be
  // huge (10k+) and may fail/timeout on big projects — never let that blank the Generate tab.
  const treeDocs = allDocuments(tree).length;
  const totalDocs = typeof counts.total === "number" ? counts.total : treeDocs;

  useEffect(() => {
    setEstimate(null);
  }, [scope, mode, moduleId]);

  // Big books (like a 10k+ row knowledge base) should pace around the daily spend limit by
  // default — pre-enable Daily Batch mode so the whole run completes across days automatically.
  useEffect(() => {
    if (totalDocs >= 500) setDailyBatches(true);
  }, [totalDocs]);

  const body = () => {
    const b = { scope, mode, daily_batches: dailyBatches };
    if (scope === "module") b.module_id = moduleId;
    return b;
  };

  const runEstimate = async () => {
    if (scope === "module" && !moduleId) {
      toast.error("Pick a module first");
      return;
    }
    setEstimating(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/estimate`, body());
      setEstimate(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setEstimating(false);
    }
  };

  const launch = async () => {
    if (scope === "module" && !moduleId) {
      toast.error("Pick a module first");
      return;
    }
    setLaunching(true);
    try {
      await api.post(`/projects/${projectId}/generate`, body());
      toast.success("Generation queue launched");
      setPreflightOpen(false);
      await reloadStatus();
      await reloadTree();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLaunching(false);
    }
  };

  // Cost preflight: fetch a fresh estimate + credit balance, then confirm before spending.
  const openPreflight = async () => {
    if (scope === "module" && !moduleId) {
      toast.error("Pick a module first");
      return;
    }
    setPreparing(true);
    try {
      const [{ data: est }, me] = await Promise.all([
        api.post(`/projects/${projectId}/estimate`, body()),
        api.get("/billing/me").then((r) => r.data).catch(() => null),
      ]);
      if (!est.doc_count) {
        toast.info("Nothing to generate for this scope — every document is already done.");
        return;
      }
      setPreflight(est);
      setEstimate(est);
      setBalance(me ? me.balance : null);
      setUnlimited(!!(me && me.unlimited));
      setPreflightOpen(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPreparing(false);
    }
  };

  const control = async (cmd) => {
    if (!activeJob) return;
    await api.post(`/jobs/${activeJob.id}/${cmd}`);
    await reloadStatus();
    toast.success(`Job ${cmd}d`);
  };

  if (totalDocs === 0) {
    return (
      <div className="animate-fade-in rounded-sm border border-dashed border-border py-20 text-center text-muted-foreground">
        Build your index first — there are no documents to generate yet.
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-8">
      {!isAdmin && !meUnlimited && meBalance != null && totalDocs > 0 && (() => {
        const enough = meBalance >= totalDocs;
        const low = meBalance < 50 || !enough;
        const pct = Math.min(100, Math.round((meBalance / Math.max(totalDocs, 1)) * 100));
        return (
          <div
            className={`rounded-sm border px-5 py-4 ${low ? "border-destructive/40 bg-destructive/5" : "border-[hsl(var(--matcha))]/40 bg-[hsl(var(--matcha)/0.06)]"}`}
            data-testid="credits-meter"
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm">
                <Coins size={16} className={low ? "text-destructive" : "text-[hsl(var(--matcha))]"} />
                <span className="font-medium" data-testid="credits-meter-balance">{meBalance.toLocaleString()} credits</span>
                <span className="text-muted-foreground">
                  · this run needs {totalDocs.toLocaleString()}
                  {enough ? " — you're covered" : ` — short by ${(totalDocs - meBalance).toLocaleString()}`}
                </span>
              </div>
              {low && (
                <Link to="/billing" data-testid="credits-meter-topup">
                  <Button size="sm" className="rounded-sm">
                    <Coins size={14} className="mr-1.5" /> Top up credits
                  </Button>
                </Link>
              )}
            </div>
            <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-secondary">
              <div
                className={`h-full rounded-full transition-[width] duration-500 ease-out ${low ? "bg-destructive" : "bg-[hsl(var(--matcha))]"}`}
                style={{ width: `${Math.max(3, pct)}%` }}
                data-testid="credits-meter-bar"
              />
            </div>
          </div>
        );
      })()}
      {activeJob && (
        <div className="rounded-md border border-primary/40 bg-primary/5 p-5" data-testid="active-job-card">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-2.5">
              <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-[hsl(var(--processor-active))] status-pulse" />
              <div>
                <div className="font-display text-lg font-semibold">Processor running</div>
                <div className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground" data-testid="job-progress-text">
                  {activeJob.completed} of {activeJob.total} documents · {activeJob.mode.replace("_", " ")} · {activeJob.status}
                  {activeJob.generating > 0 && (
                    <span className="ml-1 text-[hsl(var(--processor-active))]">· {activeJob.generating} in flight</span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              {activeJob.status === "running" ? (
                <Button size="sm" variant="outline" className="rounded-sm" onClick={() => control("pause")} data-testid="job-pause">
                  <Pause size={15} className="mr-1" /> Pause
                </Button>
              ) : (
                <Button size="sm" variant="outline" className="rounded-sm" onClick={() => control("resume")} data-testid="job-resume">
                  <Play size={15} className="mr-1" /> Resume
                </Button>
              )}
              <Button size="sm" variant="outline" className="rounded-sm text-destructive" onClick={() => control("cancel")} data-testid="job-cancel">
                <Square size={14} className="mr-1" /> Cancel
              </Button>
            </div>
          </div>

          {/* Live telemetry readout */}
          <div className="mt-5 grid grid-cols-2 gap-px overflow-hidden rounded-md border border-border/60 bg-border/50 sm:grid-cols-3 lg:grid-cols-5" data-testid="telemetry-grid">
            <div className="bg-card px-4 py-3">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Processed</div>
              <div className="mt-1 font-mono text-lg font-semibold tracking-tight" data-testid="telemetry-processed">
                {(activeJob.completed || 0).toLocaleString()}<span className="text-muted-foreground"> / {(activeJob.total || 0).toLocaleString()}</span>
              </div>
            </div>
            <div className="bg-card px-4 py-3">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Complete</div>
              <div className="mt-1 font-mono text-lg font-semibold tracking-tight">
                {activeJob.total ? Math.round((activeJob.completed / activeJob.total) * 100) : 0}%
              </div>
            </div>
            <div className="bg-card px-4 py-3">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">In flight</div>
              <div className="mt-1 font-mono text-lg font-semibold tracking-tight text-[hsl(var(--processor-active))]">
                {activeJob.generating || 0}
              </div>
            </div>
            <div className="bg-card px-4 py-3">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Rate</div>
              <div className="mt-1 font-mono text-lg font-semibold tracking-tight" data-testid="telemetry-rate">
                {activeJob.docs_per_min != null ? activeJob.docs_per_min : "—"}
                <span className="text-xs font-normal text-muted-foreground"> docs/min</span>
              </div>
            </div>
            <div className="bg-card px-4 py-3">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">ETA</div>
              <div className="mt-1 font-mono text-lg font-semibold tracking-tight">
                {activeJob.status !== "running"
                  ? "Paused"
                  : activeJob.eta_seconds != null
                  ? fmtEta(activeJob.eta_seconds)
                  : "—"}
              </div>
            </div>
          </div>

          <div className="relative mt-4">
            <Progress value={activeJob.total ? (activeJob.completed / activeJob.total) * 100 : 0} className="h-2" />
            {activeJob.status === "running" && (
              <div className="pointer-events-none absolute inset-x-0 -bottom-0.5 h-0.5 pipeline-flow opacity-70" />
            )}
          </div>
          {activeJob.error && (
            <div className="mt-3 rounded-sm border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-700" data-testid="job-error-note">
              {activeJob.error}
            </div>
          )}
          <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
            <span data-testid="job-percent">
              {activeJob.total ? Math.round((activeJob.completed / activeJob.total) * 100) : 0}% complete
            </span>
            <span className="flex items-center gap-1" data-testid="job-eta">
              <Clock size={12} />
              {activeJob.status !== "running"
                ? "Paused"
                : activeJob.eta_seconds != null
                ? `~${fmtEta(activeJob.eta_seconds)} remaining`
                : "Estimating…"}
            </span>
          </div>
        </div>
      )}

      <div>
        <h3 className="mb-3 text-xs uppercase tracking-[0.2em] text-muted-foreground">Generation mode</h3>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {MODES.map((m) => {
            const active = mode === m.id;
            return (
              <button
                key={m.id}
                onClick={() => setMode(m.id)}
                data-testid={`mode-${m.id}`}
                className={`rounded-sm border p-5 text-left transition-colors ${
                  active ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/40"
                }`}
              >
                <m.icon size={20} className={active ? "text-primary" : "text-muted-foreground"} />
                <div className="mt-3 font-serif text-lg font-semibold">{m.name}</div>
                <div className="text-xs uppercase tracking-wider text-primary">{m.tag}</div>
                <p className="mt-2 text-sm text-muted-foreground">{m.desc}</p>
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-sm border border-border bg-card p-6">
        <h3 className="mb-4 text-xs uppercase tracking-[0.2em] text-muted-foreground">Scope</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger className="rounded-sm" data-testid="scope-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="full">Full project ({totalDocs} docs)</SelectItem>
              <SelectItem value="missing">Missing / failed only</SelectItem>
              <SelectItem value="module">A specific module</SelectItem>
            </SelectContent>
          </Select>
          {scope === "module" && (
            <Select value={moduleId} onValueChange={setModuleId}>
              <SelectTrigger className="rounded-sm" data-testid="module-select">
                <SelectValue placeholder="Choose module" />
              </SelectTrigger>
              <SelectContent>
                {modules.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Button variant="outline" onClick={runEstimate} disabled={estimating} className="rounded-sm" data-testid="estimate-button">
            {estimating ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Calculator size={16} className="mr-2" />}
            Estimate cost
          </Button>
          <Button onClick={openPreflight} disabled={preparing || !!activeJob} className="rounded-sm" data-testid="launch-button">
            {preparing ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Play size={16} className="mr-2" />}
            Launch queue
          </Button>
        </div>

        {estimate && (
          <div className="mt-6 grid grid-cols-2 gap-4 border-t border-border pt-6 sm:grid-cols-4" data-testid="estimate-result">
            <Stat label="Documents" value={estimate.doc_count} />
            <Stat label="Est. words" value={estimate.est_words.toLocaleString()} />
            <Stat label="Est. time" value={`~${Math.ceil(estimate.est_time_seconds / 60)} min`} />
            <Stat label="Est. AI cost" value={`$${estimate.est_cost_usd}`} />
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
        {["waiting", "generating", "generated", "reviewing", "approved", "failed"].map((s) => (
          <div key={s} className="rounded-sm border border-border bg-card p-3 text-center">
            <div className="font-serif text-xl font-semibold">{counts[s] || 0}</div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{s}</div>
          </div>
        ))}
      </div>

      <Dialog open={preflightOpen} onOpenChange={setPreflightOpen}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="preflight-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Confirm generation run</DialogTitle>
            <DialogDescription>
              Review the estimate before spending AI budget and credits.
            </DialogDescription>
          </DialogHeader>
          {preflight && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 rounded-sm border border-border bg-secondary/30 p-4">
                <Stat label="Documents" value={preflight.doc_count} testid="preflight-docs" />
                <Stat label="Est. words" value={preflight.est_words.toLocaleString()} />
                <Stat label="Est. time" value={`~${Math.ceil(preflight.est_time_seconds / 60)} min`} />
                <Stat label="Est. AI cost" value={`$${preflight.est_cost_usd}`} testid="preflight-cost" />
              </div>
              <div className="flex items-center justify-between rounded-sm border border-border px-4 py-3 text-sm">
                <span className="flex items-center gap-2 text-muted-foreground">
                  <Coins size={15} className="text-primary" /> Credits needed
                </span>
                <span className="font-medium" data-testid="preflight-credits-needed">
                  {free ? (isAdmin ? "0 (admin — free)" : "0 (unlimited)") : preflight.doc_count.toLocaleString()}
                </span>
              </div>
              {!free && balance != null && (
                <div className="flex items-center justify-between px-1 text-sm text-muted-foreground">
                  <span>Your balance</span>
                  <span data-testid="preflight-balance">{balance.toLocaleString()} credits</span>
                </div>
              )}
              {preflight.est_cost_usd >= 50 && (
                <div className="flex items-start gap-2 rounded-sm border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-700" data-testid="preflight-cost-warning">
                  <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                  <span>This is a large run (~${preflight.est_cost_usd} in AI spend). Consider generating in daily batches or splitting the book.</span>
                </div>
              )}
              {!free && balance != null && balance < preflight.doc_count && (
                <div className="flex items-start gap-2 rounded-sm border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="preflight-insufficient">
                  <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                  <span>Not enough credits — you need {preflight.doc_count.toLocaleString()} but have {balance.toLocaleString()}. Buy a pack or subscribe from Billing.</span>
                </div>
              )}
              <div className="flex items-start justify-between gap-3 rounded-sm border border-border px-3 py-3" data-testid="daily-batch-row">
                <div className="min-w-0">
                  <div className="text-sm font-medium">Generate in daily batches</div>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    If the AI provider's daily spend limit is hit, this run pauses and resumes automatically each day until the whole book is done.
                  </p>
                </div>
                <Switch checked={dailyBatches} onCheckedChange={setDailyBatches} data-testid="daily-batch-switch" />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => setPreflightOpen(false)} data-testid="preflight-cancel">
              Cancel
            </Button>
            <Button
              className="rounded-sm"
              onClick={launch}
              disabled={launching || (!free && balance != null && preflight && balance < preflight.doc_count)}
              data-testid="preflight-confirm"
            >
              {launching ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Play size={16} className="mr-2" />}
              Start generation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function fmtEta(s) {
  if (s == null) return "—";
  if (s < 60) return `${Math.max(1, Math.round(s))}s`;
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  if (m < 60) return sec ? `${m}m ${sec}s` : `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function Stat({ label, value, testid }) {
  return (
    <div>
      <div className="font-serif text-2xl font-semibold" data-testid={testid}>{value}</div>
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}
